import React from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import WarningIcon from "@mui/icons-material/Warning";
import { Chip, Stack, Tooltip } from "@mui/material";

export interface HealthCounts {
  running: number;
  stopped: number;
  failed: number;
  /** Any other status (draft, provisioning, etc.) */
  other: number;
}

interface HealthSummaryChipsProps {
  counts: HealthCounts;
  /** If true, only show chips with count > 0 */
  hideZero?: boolean;
}

/**
 * HealthSummaryChips — a row of compact status chips summarising stack health.
 * Used in the org overview and project health rows.
 */
export function HealthSummaryChips({
  counts,
  hideZero = true,
}: HealthSummaryChipsProps) {
  const chips: {
    key: keyof HealthCounts;
    label: string;
    color: "success" | "default" | "error" | "warning";
    icon: React.ReactElement;
  }[] = [
    {
      key: "running",
      label: "Running",
      color: "success",
      icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
    },
    {
      key: "stopped",
      label: "Stopped",
      color: "default",
      icon: <PauseCircleIcon sx={{ fontSize: 14 }} />,
    },
    {
      key: "failed",
      label: "Failed",
      color: "error",
      icon: <ErrorIcon sx={{ fontSize: 14 }} />,
    },
    {
      key: "other",
      label: "Other",
      color: "warning",
      icon: <WarningIcon sx={{ fontSize: 14 }} />,
    },
  ];

  const visible = chips.filter((c) => !hideZero || counts[c.key] > 0);
  if (visible.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap">
      {visible.map(({ key, label, color, icon }) => (
        <Tooltip
          key={key}
          title={`${counts[key]} stack${counts[key] === 1 ? "" : "s"} currently ${label.toLowerCase()}`}
        >
          <Chip
            icon={icon}
            label={`${counts[key]} ${label}`}
            size="small"
            color={color}
            variant={key === "running" ? "filled" : "outlined"}
            sx={{ fontSize: "0.7rem", height: 22 }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

/** Compute HealthCounts from an array of stack status strings. */
export function computeHealthCounts(statuses: string[]): HealthCounts {
  const counts: HealthCounts = { running: 0, stopped: 0, failed: 0, other: 0 };
  for (const s of statuses) {
    const lower = s.toLowerCase();
    if (lower === "running") counts.running++;
    else if (lower === "stopped" || lower === "paused") counts.stopped++;
    else if (lower === "failed") counts.failed++;
    else counts.other++;
  }
  return counts;
}
